import * as React from "react";
import { DateRange } from "react-day-picker";
import { Button } from "@volca/ui/components/ui/button";
import { Calendar } from "@volca/ui/components/ui/calendar";
import { Field, FieldLabel } from "@volca/ui/components/ui/field";
import { Popover, PopoverContent } from "@volca/ui/components/ui/popover";
import { Stack } from "@volca/ui/components/ui/stack";
import { parseTypeableDate } from "@volca/ui/components/ui/typeable-date-picker";
import { useBreakpoint } from "@volca/ui/hooks/use-breakpoint";
import { dayjs, FORMAT_TYPEABLE_DATE } from "@volca/ui/lib/date";
import { cn } from "@volca/ui/lib/utils";
import { PresetList } from "./preset-list";
import {
  DEFAULT_PRESETS,
  type TimeRange,
  type TimeRangePreset,
} from "./presets";
import { TimeRangeTrigger } from "./time-range-trigger";
import { TypedRangeFields } from "./typed-range-fields";
import type { TimeRangePickerProps } from "./types";
import {
  anchorMonth,
  createDateLabel,
  MONTHS_ON_SCREEN,
  RANGE_END,
  type RangeEnd,
} from "./utils";

export type { TimeRange, TimeRangePreset } from "./presets";

const YEAR_DROPDOWN_END = dayjs().add(12, "year").endOf("year").toDate();

const TimeRangePicker = React.forwardRef<
  HTMLButtonElement,
  TimeRangePickerProps
>(
  (
    {
      value,
      onChange,
      onSelect,
      label,
      placeholder = "Select time range",
      required = false,
      fullWidth = false,
      presets = DEFAULT_PRESETS,
      allowTextEntry = false,
      variant = "field",
    },
    ref,
  ) => {
    const isMobile = useBreakpoint() === "mobile";
    const monthsOnScreen = isMobile ? 1 : MONTHS_ON_SCREEN;
    const pickerId = React.useId();
    const labelId = `${pickerId}-label`;
    const valueId = `${pickerId}-value`;
    const [open, setOpen] = React.useState(false);
    const [internalRange, setInternalRange] = React.useState<{
      from?: Date;
      to?: Date;
    }>({});
    const [internalPreset, setInternalPreset] = React.useState<string | null>(
      null,
    );
    const [internalLabel, setInternalLabel] = React.useState<string | null>(
      null,
    );
    const [visibleMonth, setVisibleMonth] = React.useState<Date>(() =>
      dayjs()
        .subtract(monthsOnScreen - 1, "month")
        .toDate(),
    );
    const [draft, setDraft] = React.useState<{
      end: RangeEnd;
      text: string;
    } | null>(null);

    React.useEffect(() => {
      if (open) {
        if (value) {
          setInternalRange({ from: value.from, to: value.to });
          const matchingPreset = presets.find((preset) => {
            const presetValue = preset.getValue();
            return (
              dayjs(presetValue.from).isSame(value.from, "day") &&
              dayjs(presetValue.to).isSame(value.to, "day")
            );
          });
          setInternalPreset(matchingPreset?.label || null);
          setInternalLabel(createDateLabel(value.from, value.to));
          setVisibleMonth(
            anchorMonth(
              RANGE_END.to,
              value.to,
              { from: value.from, to: value.to },
              monthsOnScreen,
            ),
          );
        } else {
          setVisibleMonth(
            dayjs()
              .subtract(monthsOnScreen - 1, "month")
              .toDate(),
          );
        }
      }
    }, [open, value, presets, monthsOnScreen]);

    const handleOpenChange = (newOpen: boolean) => {
      if (!newOpen && open) {
        if (internalRange.from && internalRange.to) {
          handleApplyInternal();
        }
      }
      setOpen(newOpen);
      if (!newOpen) {
        setInternalRange({});
        setInternalPreset(null);
        setInternalLabel(null);
        setDraft(null);
      }
    };

    const handleClear = (e?: React.MouseEvent) => {
      e?.stopPropagation();
      (onSelect || onChange)?.(null);
      setInternalRange({});
      setInternalPreset(null);
      setInternalLabel(null);
      setDraft(null);
    };

    const displayValue =
      open && internalLabel
        ? internalLabel
        : value
          ? createDateLabel(value.from, value.to)
          : placeholder;

    const handlePresetClick = (preset: TimeRangePreset) => {
      const range = preset.getValue();
      setInternalRange({ from: range.from, to: range.to });
      setInternalPreset(preset.label);
      setInternalLabel(createDateLabel(range.from, range.to));
      setVisibleMonth(
        anchorMonth(RANGE_END.to, range.to, range, monthsOnScreen),
      );
    };

    const handleCalendarSelect = (range?: { from?: Date; to?: Date }) => {
      if (!range?.from) return;

      if (!range.to) {
        const singleDayRange = {
          from: range.from,
          to: range.from,
        };
        setInternalRange(singleDayRange);
        setInternalPreset(null);
        setInternalLabel(
          createDateLabel(singleDayRange.from, singleDayRange.to),
        );
        return;
      }

      setInternalRange(range);
      setInternalPreset(null);

      const finalFrom = dayjs(range.to).isBefore(range.from)
        ? range.to
        : range.from;
      const finalTo = dayjs(range.to).isBefore(range.from)
        ? range.from
        : range.to;

      setInternalLabel(createDateLabel(finalFrom, finalTo));
    };

    const textFor = (end: RangeEnd) => {
      if (draft?.end === end) return draft.text;
      const date = internalRange[end];
      return date ? dayjs(date).format(FORMAT_TYPEABLE_DATE) : "";
    };

    const handleTypedChange = (end: RangeEnd, text: string) => {
      setDraft({ end, text });

      const parsed = parseTypeableDate(text);
      if (!parsed) return;

      const next = { ...internalRange, [end]: parsed };
      setInternalRange(next);
      setInternalPreset(null);
      setInternalLabel(
        next.from && next.to ? createDateLabel(next.from, next.to) : null,
      );
      setVisibleMonth(anchorMonth(end, parsed, next, monthsOnScreen));
    };

    const handleApplyInternal = () => {
      if (internalRange.from && internalRange.to) {
        const finalFrom = dayjs(internalRange.to).isBefore(internalRange.from)
          ? internalRange.to
          : internalRange.from;
        const finalTo = dayjs(internalRange.to).isBefore(internalRange.from)
          ? internalRange.from
          : internalRange.to;

        const timeRange: TimeRange = {
          from: finalFrom,
          to: finalTo,
          label: createDateLabel(finalFrom, finalTo),
        };

        (onSelect || onChange)?.(timeRange);
      }
    };

    const handleApply = () => {
      handleApplyInternal();
      setInternalRange({});
      setInternalPreset(null);
      setInternalLabel(null);
      setDraft(null);
      setOpen(false);
    };

    return (
      <Field
        className={cn({
          "w-full": fullWidth && variant === "field",
          "w-68": !fullWidth && variant === "field",
          "w-fit": variant === "filter",
        })}
      >
        {label && variant === "field" && (
          <FieldLabel id={labelId} htmlFor={pickerId} className="gap-0.5">
            {label}
            {required && <span aria-hidden="true">*</span>}
          </FieldLabel>
        )}
        <Popover
          open={open}
          onOpenChange={(next: boolean) => handleOpenChange(next)}
        >
          <TimeRangeTrigger
            ref={ref}
            id={pickerId}
            labelId={label ? labelId : undefined}
            valueId={valueId}
            displayValue={displayValue}
            hasValue={Boolean(value)}
            open={open}
            onClear={handleClear}
            variant={variant}
            label={label}
          />
          <PopoverContent
            aria-label={label ?? "Date range"}
            // Scrolls when it can't fit, as from a trigger low in a dialog on a
            // phone; otherwise its Done button lands off-screen.
            className="max-h-(--available-height) w-auto max-w-[calc(100vw-1rem)] gap-0 overflow-x-hidden overflow-y-auto p-0"
            align="start"
            initialFocus={false}
          >
            {/* Phones: presets above a single month, so the popup fits. */}
            <Stack gap={0} align="stretch" className="sm:flex-row">
              <PresetList
                presets={presets}
                selected={internalPreset}
                onSelect={handlePresetClick}
              />

              {/* Right side with calendar */}
              <div className="p-3">
                {allowTextEntry && (
                  <TypedRangeFields
                    idPrefix={pickerId}
                    textFor={textFor}
                    onTypedChange={handleTypedChange}
                    onBlur={() => setDraft(null)}
                  />
                )}
                <Calendar
                  mode="range"
                  selected={internalRange as DateRange}
                  onSelect={handleCalendarSelect}
                  numberOfMonths={monthsOnScreen}
                  month={visibleMonth}
                  onMonthChange={setVisibleMonth}
                  autoFocus={!allowTextEntry}
                  captionLayout="dropdown"
                  endMonth={YEAR_DROPDOWN_END}
                />
                <Stack
                  align="start"
                  gap={4}
                  direction="row"
                  className="mt-3"
                  justify="end"
                >
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleClear}
                    disabled={!internalRange.from && !internalRange.to}
                  >
                    Clear
                  </Button>
                  <Button size="sm" onClick={handleApply}>
                    Done
                  </Button>
                </Stack>
              </div>
            </Stack>
          </PopoverContent>
        </Popover>
      </Field>
    );
  },
);

TimeRangePicker.displayName = "TimeRangePicker";

export { TimeRangePicker, DEFAULT_PRESETS };
